import React, { useRef, useState, useEffect} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EditOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import EmailEditor, { EditorRef } from "react-email-editor";
import EmailStatusPopup from "../Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailStatusPopup";
import type { EmailDesign } from "../Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignTypes";
import { exportEditorHtml, loadTemplateIntoEditor } from "../Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailEditorUtils";
import { sendCampaignEmail} from "src/services/CampaignAPI";
import { TimePicker, message } from "antd";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { DashboardAntDatePicker } from "src/components/reusable/AntdDatePicker/DashboardAntDatePicker";
import { useTranslation } from "react-i18next";
import "../Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/Styles/AIEmailEditor.scss";

interface LocationState {
  campaignName: string;
  replyTo: string;
  recipients: string;
  subjectLine: string;
  fromName: string;
  fromEmail: string;
  startDate: string | null;
  endDate: string | null;
  popupType?: "scheduled" | "sent" | null;
  emailDesign?: EmailDesign;
  emailHtml?: string;
  mode?: "manual" | "ai";
  templateType?: "json" | "html";
}

interface SchedulePreset {
  label: string;
  time: string;
  dayOffset?: number;
  kind: "tomorrowMorning" | "tomorrowEvening" | "nextMonday";
}

const ReviewAndSend: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const locationState = state as LocationState;
  const navigate = useNavigate();
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [editingField, setEditingField] = useState<string | null>(null);
const [isSending, setIsSending] = useState(false);
const [templateHTML, setTemplateHTML] = useState<EmailDesign | null>(null);
const [templateType, setTemplateType] = useState<LocationState["templateType"] | undefined>(undefined);
  const [campaignName, setCampaignName] = useState(locationState?.campaignName || "");
   const [sendTo, setSendTo] = useState<string[]>(
    locationState?.recipients
      ? locationState.recipients.split(",").map(e => e.trim())
      : []
  );
  const [fromName, setFromName] = useState(
    localStorage.getItem("company_name") || t("campaignFlow.common.defaultCompany"),
  );
  const [fromEmail, setFromEmail] = useState("marketing@datatram.ai");
  const [replyTo, setReplyTo] = useState("support@datatram.com");
  const [subjectLine, setSubjectLine] = useState("");

  const emailEditorRef = useRef<EditorRef | null>(null);
  const [popupType, setPopupType] = useState<"scheduled" | "sent" | null>(null);

  const [renderedHTML, setRenderedHTML] = useState<string | null>(null);

useEffect(() => {
  if (!locationState) {return;}

  setTemplateType(locationState.templateType);

  // If JSON exists, keep it for editor
  if (locationState.emailDesign) {
    setTemplateHTML(locationState.emailDesign);
  }

  // If HTML exists, keep it for sending
  if (locationState.emailHtml) {
    setRenderedHTML(locationState.emailHtml); // <-- Add this state
  }

}, [locationState]);

  const renderField = (
    label: string,
    value: string,
    onChange: (_v: string) => void,
    type: "text" | "email" = "text",
    fieldName: string,
    placeholder?: string
  ) => (
    <div className="summary-item" key={label}>
      <label>{label}</label>
      <div className="summary-input-wrapper">
        <div className="input-with-icon-container">
          <StyledTextInput
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="form-input-summary"
            readOnly={fieldName === "fromEmail" || editingField !== fieldName}
          />

          {fieldName !== "fromEmail" &&
            (editingField === fieldName ? (
              <CheckOutlined
                className="edit-icon inside-input"
                onClick={() => setEditingField(null)}
              />
            ) : (
              <EditOutlined
                className="edit-icon inside-input"
                onClick={() => setEditingField(fieldName)}
              />
            ))}
        </div>
      </div>
    </div>
  );

const handleSendCampaign = async (sendAt?: string) => {
  try {
    setIsSending(true);

    const html = await exportEditorHtml(emailEditorRef.current?.editor);
    if (!html) {
      message.error(t("campaignFlow.reviewAndSend.sendError"));
      return;
    }

    const payload = {
      tenant_id: 0,
      campaign_name: campaignName,
      send_to: sendTo,
      dont_send_to: [],
      html,
      send_at: sendAt || new Date().toISOString(),
      company_name_engagements: fromName,
    };

    await sendCampaignEmail(payload);

if (sendAt) {
  message.success(t("campaignFlow.reviewAndSend.campaignScheduled"));
} else {
  message.success(t("campaignFlow.reviewAndSend.campaignSent"));
}
setPopupType(sendAt ? "scheduled" : "sent");
} catch {
  message.error(t("campaignFlow.reviewAndSend.sendError"));
} finally {
  setIsSending(false);
}
};

  const getPresetDateTime = (preset: SchedulePreset) => {
    const today = new Date();
    const targetDate = new Date(today);

    if (preset.kind === "tomorrowMorning" || preset.kind === "tomorrowEvening") {
      targetDate.setDate(today.getDate() + 1);
    } else if (preset.kind === "nextMonday") {
      const dayOffset = preset.dayOffset || ((8 - today.getDay()) % 7 || 7);
      targetDate.setDate(today.getDate() + dayOffset);
    }

    const dateStr = targetDate.toISOString().split("T")[0];
    return { dateStr, time: preset.time };
  };

  const handleQuickSchedule = (preset: SchedulePreset) => {
    const { dateStr, time } = getPresetDateTime(preset);
    setScheduledDate(dateStr);
    setScheduledTime(time);
  };

const handleEditorReady = () => {
  const editor = emailEditorRef.current?.editor;
  if (!editor) {return;}

  // 1) JSON always takes priority for EDITING
  if (templateType === "json" && templateHTML) {
    editor.loadDesign(templateHTML);
    return;
  }

// 2) Fallback to HTML import if JSON not available
if (typeof renderedHTML === "string") {
  loadTemplateIntoEditor(editor, { design: null, html: renderedHTML });
}
};

  return (
    <div className="manual-review-step review-send-page">

      <div className="campaign-summary-box review-send-shell">
        <div className="page-header">
          <button
            className="back-button back-button--text button button--primary"
            onClick={() => navigate(-1)}
          >
            {t("campaignFlow.common.back")}
          </button>

          <span className="Page-header-text">
            {t("campaignFlow.common.aiReviewBreadcrumb")}
          </span>
        </div>

        <h2 className="form-title review-send-title-row">
          {t("campaignFlow.common.reviewAndSendCampaignSummary")}
        </h2>
        <div className="review-step">
          <div className="Manual-summary-box">
            <div className="summary-grid">
              {renderField(t("campaignFlow.common.campaignName"), campaignName, setCampaignName, "text", "campaignName")}
              {renderField(t("campaignFlow.common.replyTo"), replyTo, setReplyTo, "email", "replyTo")}
              {renderField(t("campaignFlow.common.recipients"),sendTo.join(", "),v => setSendTo(v.split(",").map(e => e.trim())),"text","recipients")}
              {renderField(t("campaignFlow.common.subjectLine"), subjectLine, setSubjectLine, "text", "subjectLine", t("campaignFlow.aiWizard.details.subjectLinePlaceholder"))}
              {renderField(t("campaignFlow.common.fromName"), fromName, setFromName, "text", "fromName")}
              {renderField(t("campaignFlow.common.fromEmail"), fromEmail, setFromEmail, "email", "fromEmail")}
            </div>

           <div className="email-editor-container">
             <EmailEditor ref={emailEditorRef} onLoad={handleEditorReady} options={{ displayMode: "email" }} /> 
             </div>
          </div>

          <div className="Email-footer-actions">
            <button
              className="button button--primary"
              onClick={() => setShowSchedulePopup(true)}
            >
              {t("campaignFlow.common.scheduleSendUpper")}
            </button>
            <button
              className="button button--primary"
              onClick={async () => {
                await handleSendCampaign(); // send instantly
              }}
              disabled={isSending}
            >
              {isSending ? t("campaignFlow.common.sending") : t("campaignFlow.common.sendNowUpper")}
            </button>
          </div>

          {popupType && <EmailStatusPopup type={popupType} onClose={() => setPopupType(null)} />}

          {showSchedulePopup && (
            <div className="schedule-modal-overlay">
              <div className="schedule-modal">
                <h4 className="schedule-picker-title">{t("campaignFlow.common.scheduleCampaign")}</h4>
                <p className="schedule-picker-paragraph">{t("campaignFlow.common.pakistanStandardTime")}</p>

                <div className="schedule-picker-presets">
                  {[
                    {
                      label: `${t("campaignFlow.common.tomorrowMorning")} 9:00 AM`,
                      time: "09:00",
                      kind: "tomorrowMorning" as const,
                    },
                    {
                      label: `${t("campaignFlow.common.tomorrowEvening")} 6:00 PM`,
                      time: "18:00",
                      kind: "tomorrowEvening" as const,
                    },
                    {
                      label: `${t("campaignFlow.common.nextMondayMorning")} 9:00 AM`,
                      time: "09:00",
                      dayOffset: (8 - new Date().getDay()) % 7 || 7,
                      kind: "nextMonday" as const,
                    },
                  ].map((preset, index) => {
                    const { dateStr, time } = getPresetDateTime(preset);
                    const isSelected =
                      scheduledDate === dateStr && scheduledTime === time;
                    return (
                      <button
                        key={index}
                        className={`preset-button ${
                          isSelected ? "preset-button--active" : ""
                        }`}
                        onClick={() => handleQuickSchedule(preset)}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>

                <label className="schedule-label">{t("campaignFlow.common.selectDate")}</label>
                <DashboardAntDatePicker
                  className="schedule-input"
                  value={scheduledDate ? dayjs(scheduledDate) : null}
                  onChange={date => setScheduledDate(date ? date.format("YYYY-MM-DD") : "")}
                  disabledDate={current => current.isBefore(dayjs().startOf("day"))}
                />

                <label className="schedule-label">{t("campaignFlow.common.selectTime")}</label>
                <TimePicker
                  className="schedule-input"
                  value={scheduledTime ? dayjs(scheduledTime, "HH:mm") : null}
                  onChange={time => setScheduledTime(time ? time.format("HH:mm") : "")}
                  format="hh:mm A"
                  use12Hours
                  needConfirm={false}
                />

                <div className="schedule-actions">
                  <button
                    className="button button--primary proceed-button"
                    onClick={async () => {
                      if (!scheduledDate || !scheduledTime) {return;}
                      const scheduleDateTime = new Date(
                        `${scheduledDate}T${scheduledTime}:00Z`
                      ).toISOString();

                      await handleSendCampaign(scheduleDateTime);
                      setShowSchedulePopup(false); // close the modal after success
                    }}
                    disabled={!scheduledDate || !scheduledTime || isSending}
                  >
                    {isSending ? t("campaignFlow.common.scheduling") : t("campaignFlow.common.schedule")}
                  </button>

                  <button
                    className="button button--primary proceed-button"
                    onClick={() => setShowSchedulePopup(false)}
                  >
                    {t("campaignFlow.common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewAndSend;
